// Google Analytics 4 integration. Stays fully dormant when
// `VITE_GA_MEASUREMENT_ID` isn't set or when the browser advertises
// Do Not Track, so dev builds and privacy-respecting users incur zero
// network calls. Once initialised, exposes:
//
//   - trackPageview(path)         — fire on every route change.
//   - trackEvent(name, params)    — generic custom events.
//
// We inject gtag.js directly (no react-ga4 dependency) to keep the
// bundle slim. The script is loaded with `async` so it never blocks
// first paint.
//
// IMPORTANT: the gtag shim MUST push raw `arguments` (not a spread
// array) onto dataLayer. gtag.js's loader recognises only the
// arguments-object shape as a variadic call; an array literal is
// silently dropped from the queue and the whole pipeline (config +
// events) becomes a no-op. This was the qlearn-specific bug that
// caused "gtm.load fires but no /g/collect ever sends" — diagnosed by
// comparing against the working files.smndiaye implementation.

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

interface InitOptions {
  measurementId: string
}

let initialised = false
let measurementId = ''

function dntEnabled(): boolean {
  if (typeof window === 'undefined' || !window.navigator) return false
  const nav = window.navigator as Navigator & { msDoNotTrack?: string; doNotTrack?: string }
  const value = nav.doNotTrack ?? nav.msDoNotTrack ?? (window as unknown as { doNotTrack?: string }).doNotTrack
  return value === '1' || value === 'yes'
}

/** Idempotent. Loads the gtag.js script and registers the GA4 config. */
export function initAnalytics(opts: InitOptions): void {
  if (initialised) return
  if (!opts.measurementId) return
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (dntEnabled()) return

  measurementId = opts.measurementId

  window.dataLayer = window.dataLayer ?? []
  // Mirrors the official Google snippet. `arguments` (not `...args`) is
  // load-bearing — see the file-level note above for why.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  }
  window.gtag('js', new Date())
  // `send_page_view: false` because we fire pageviews manually from the
  // React Router listener (see <AnalyticsPageviews /> in App.tsx) —
  // otherwise the first SPA route would log twice (once auto, once manual).
  window.gtag('config', measurementId, { send_page_view: false })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  script.dataset.ga = '1'
  document.head.appendChild(script)

  initialised = true
}

/** Manually fire a pageview. Path defaults to current location.
 *  No-op when analytics aren't initialised. */
export function trackPageview(path?: string): void {
  if (!initialised || !window.gtag) return
  const page_path = path ?? window.location.pathname + window.location.search
  window.gtag('event', 'page_view', {
    page_path,
    page_location: window.location.origin + page_path,
    page_title: document.title,
  })
}

/** Generic custom-event helper. No-op when analytics aren't initialised. */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (!initialised || !window.gtag) return
  window.gtag('event', name, params ?? {})
}

/** Set GA4 user properties (e.g. `display_mode`). No-op when analytics aren't
 *  initialised. Unlike events, user properties stick to the user across
 *  sessions and are what GA4 audiences/segments key off — so this is how we
 *  flag "this user runs the installed PWA". */
export function setUserProperties(props: Record<string, unknown>): void {
  if (!initialised || !window.gtag) return
  window.gtag('set', 'user_properties', props)
}

/** Whether analytics is configured for this build. */
export const analyticsEnabled = Boolean(import.meta.env.VITE_GA_MEASUREMENT_ID)

/** Test-only escape hatch. Vitest reuses one JSDOM instance across cases
 *  in a file; without this, subsequent `initAnalytics()` calls early-return
 *  because `initialised` from the previous test is still true. */
export function resetAnalyticsForTests(): void {
  initialised = false
  measurementId = ''
}
